// RAPIFAST :: reintento post-login del registro complementario del negocio.
// Se monta solo para rol BUSINESS (RoleLayout). Silencioso mientras 0024 no exista.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CircleAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { readBusinessDraft, syncBusinessVerification, clearBusinessDraft } from "@/lib/businessOnboarding";

export const BusinessOnboardingSync = () => {
  const { session, profile } = useAuth();
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const email = session?.user?.email;

  const attempt = useCallback(async () => {
    if (!email || !profile) return;
    if (!readBusinessDraft(email)) return; // sin borrador pendiente: nada que hacer
    setBusy(true);
    try {
      const r = await syncBusinessVerification({
        email,
        fullName: profile.full_name,
        phone: profile.phone,
        businessName: profile.business_name,
      });
      if (r.status === "synced") {
        setFailed(false);
        toast.success("Datos del negocio registrados para verificación.");
      } else if (r.status === "error") {
        setFailed(true);
      }
      // "pending_migration" (0024 aún no aplicada): silencio intencional.
    } finally {
      setBusy(false);
    }
  }, [email, profile]);

  // Un intento automático por sesión/correo; el botón Reintentar sigue disponible.
  const triedRef = useRef(null);
  useEffect(() => {
    if (!email || !profile || triedRef.current === email) return;
    triedRef.current = email;
    attempt();
  }, [email, profile, attempt]);

  if (!failed) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3"
      data-testid="business-onboarding-banner">
      <CircleAlert size={18} className="shrink-0 text-amber-400" />
      <div className="grow text-xs leading-relaxed text-amber-200/90">
        Los datos complementarios de tu negocio (dirección, municipio y aceptación de documentos)
        aún no se han podido guardar. Tu cuenta y tus servicios no se ven afectados; puedes reintentarlo ahora.
      </div>
      <button onClick={attempt} disabled={busy} data-testid="business-onboarding-retry"
        className="rounded-lg border border-amber-500/50 px-3 py-1.5 text-xs font-bold text-amber-300 hover:bg-amber-500/10 disabled:opacity-40">
        {busy ? "Guardando…" : "Reintentar"}
      </button>
      <button
        onClick={() => {
          clearBusinessDraft(email); // el usuario descarta el transporte pendiente
          setFailed(false);
          toast.info("Datos temporales descartados. Puedes completarlos cuando quieras desde tu registro o panel.");
        }}
        disabled={busy}
        data-testid="business-onboarding-discard"
        className="rounded-lg border border-[#333] px-3 py-1.5 text-xs font-bold text-gray-400 hover:bg-[#1F1F1F] disabled:opacity-40"
      >
        Descartar
      </button>
    </div>
  );
};
